import React, { useCallback, useRef, useState } from "react";
import { Button, Input, Spin, Tag, Typography, Space } from "antd";
import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { productsService } from "../../../../shared/services";
import {
  CategoryConfig,
  ExcludedProduct,
} from "../../hooks/useCreatePromotion";

const { Text } = Typography;
const SEARCH_DEBOUNCE_MS = 400;

interface Props {
  excludedProducts: ExcludedProduct[];
  onChange: (products: ExcludedProduct[]) => void;
  categories: CategoryConfig[];
}

export const PromotionExcludedProducts: React.FC<Props> = ({
  excludedProducts,
  onChange,
  categories,
}) => {
  const [searchOptions, setSearchOptions] = useState<ExcludedProduct[]>([]);
  const [fetching, setFetching] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const categoryIds = categories
    .filter((c) => c.category_id > 0)
    .map((c) => c.category_id);

  const searchProducts = useCallback(
    (searchTerm: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (!searchTerm.trim()) {
        setSearchOptions([]);
        return;
      }

      debounceRef.current = setTimeout(() => {
        setFetching(true);
        const query: Record<string, unknown> = {
          status: "active",
          data_value: searchTerm.trim(),
          $limit: 30,
          $select: ["id", "name", "category_id", "category_id_2", "category_id_3"],
        };

        if (categoryIds.length > 0) {
          query.$or = categoryIds.flatMap((id) => [
            { category_id: id },
            { category_id_2: id },
            { category_id_3: id },
          ]);
        }

        productsService
          .find({
            query: {
              ...query,
              $client: { skipJoins: true },
            },
          })
          .then((res: any) => {
            const raw = res?.data ?? res;
            const data = Array.isArray(raw) ? raw : raw?.data ?? [];
            const excludedIds = new Set(excludedProducts.map((p) => p.id));
            setSearchOptions(
              data
                .filter((p: any) => !excludedIds.has(p.id))
                .map((p: any) => ({ id: p.id, name: p.name })),
            );
          })
          .catch(() => setSearchOptions([]))
          .finally(() => {
            setFetching(false);
            debounceRef.current = null;
          });
      }, SEARCH_DEBOUNCE_MS);
    },
    [categoryIds, excludedProducts],
  );

  const handleSearch = (term: string) => {
    setSearchValue(term);
    searchProducts(term);
  };

  const handleAdd = (product: ExcludedProduct) => {
    if (excludedProducts.some((p) => p.id === product.id)) return;
    onChange([...excludedProducts, product]);
    setSearchOptions((prev) => prev.filter((p) => p.id !== product.id));
  };

  const handleRemove = (productId: number) => {
    onChange(excludedProducts.filter((p) => p.id !== productId));
  };

  const showResults =
    searchValue.trim().length > 0 && (fetching || searchOptions.length > 0);

  return (
    <div>
      <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
        Estos productos no participan en la promoción. En la app se cobrarán a tarifa
        plena y se informará que el rollo no aplica.
      </Text>

      <Input
        allowClear
        prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
        placeholder="Buscar producto para excluir de la promo..."
        value={searchValue}
        onChange={(e) => handleSearch(e.target.value)}
        style={{ marginBottom: 8 }}
      />

      {fetching && (
        <div style={{ textAlign: "center", padding: 12 }}>
          <Spin size="small" />
        </div>
      )}

      {showResults && !fetching && searchOptions.length > 0 && (
        <div
          style={{
            border: "1px solid #d9d9d9",
            borderRadius: 8,
            maxHeight: 220,
            overflowY: "auto",
            marginBottom: 12,
            background: "#fff",
          }}
        >
          {searchOptions.map((p) => (
            <div
              key={p.id}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
                padding: "8px 12px",
                borderBottom: "1px solid #f0f0f0",
              }}
            >
              <Text style={{ flex: 1, fontSize: 13 }}>
                {p.name}{" "}
                <Text type="secondary" style={{ fontSize: 12 }}>
                  (#{p.id})
                </Text>
              </Text>
              <Button
                type="link"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => handleAdd(p)}
                style={{ flexShrink: 0 }}
              >
                Agregar
              </Button>
            </div>
          ))}
        </div>
      )}

      {showResults && !fetching && searchOptions.length === 0 && (
        <Text type="secondary" style={{ display: "block", marginBottom: 12, fontSize: 13 }}>
          No hay más resultados para &quot;{searchValue}&quot;
        </Text>
      )}

      {excludedProducts.length > 0 ? (
        <Space size={[8, 8]} wrap>
          {excludedProducts.map((p) => (
            <Tag
              key={p.id}
              closable
              color="orange"
              onClose={() => handleRemove(p.id)}
              style={{ padding: "4px 8px", fontSize: 13 }}
            >
              {p.name}
            </Tag>
          ))}
        </Space>
      ) : (
        <Text type="secondary" style={{ fontSize: 13 }}>
          Sin exclusiones. Todos los productos de las categorías configuradas aplican.
        </Text>
      )}
    </div>
  );
};
